import React, { useState } from 'react';
import { getAuthToken } from '../../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CopywritingModule = ({ user }) => {
  const [activeCategory, setActiveCategory] = useState('sales');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    productName: '',
    targetAudience: '',
    tone: 'professional',
    framework: 'aida',
    language: 'fr',
    wordCount: 500,
    additionalInfo: ''
  });
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [variants, setVariants] = useState([]);
  const [variantCount, setVariantCount] = useState(1);
  const [error, setError] = useState('');

  const categories = [
    { id: 'sales', name: 'Pages de vente', icon: '📄' },
    { id: 'email', name: 'Email Marketing', icon: '📧' },
    { id: 'ads', name: 'Publicités', icon: '📢' },
    { id: 'video', name: 'Scripts Vidéo', icon: '🎬' },
    { id: 'product', name: 'E-commerce', icon: '🛒' },
    { id: 'hooks', name: 'Headlines & CTA', icon: '🎯' }
  ];

  const templates = {
    sales: [
      { id: 'sales-page', name: 'Page de vente complète', desc: 'Structure persuasive avec tous les éléments de conversion', premium: true },
      { id: 'landing-page', name: 'Landing Page', desc: 'Page d\'atterrissage optimisée pour la conversion', premium: false },
      { id: 'capture-page', name: 'Page de capture', desc: 'Collecte d\'emails avec offre irrésistible', premium: false },
      { id: 'webinar-page', name: 'Page Webinaire', desc: 'Inscription webinaire à fort taux de conversion', premium: true },
      { id: 'vsl-script', name: 'Script VSL', desc: 'Video Sales Letter structurée', premium: true }
    ],
    email: [
      { id: 'sales-email', name: 'Email de vente', desc: 'Email persuasif orienté conversion', premium: false },
      { id: 'email-sequence', name: 'Séquence automatisée', desc: 'Série de 5-7 emails de nurturing', premium: true },
      { id: 'newsletter', name: 'Newsletter', desc: 'Email informatif engageant', premium: false },
      { id: 'welcome-email', name: 'Email de bienvenue', desc: 'Premier contact avec nouveaux abonnés', premium: false },
      { id: 'abandoned-cart', name: 'Panier abandonné', desc: 'Récupération de ventes e-commerce', premium: true },
      { id: 'launch-sequence', name: 'Séquence de lancement', desc: 'Emails pour lancement produit', premium: true }
    ],
    ads: [
      { id: 'facebook-ad', name: 'Publicité Facebook', desc: 'Ad copy optimisée pour Facebook/Meta', premium: false },
      { id: 'google-ad', name: 'Google Ads', desc: 'Annonces textuelles Google', premium: false },
      { id: 'instagram-ad', name: 'Publicité Instagram', desc: 'Copy visuelle pour Instagram', premium: false },
      { id: 'linkedin-ad', name: 'Publicité LinkedIn', desc: 'B2B ads professionnelles', premium: true },
      { id: 'tiktok-ad', name: 'Publicité TikTok', desc: 'Copy native TikTok', premium: true }
    ],
    video: [
      { id: 'youtube-script', name: 'Script YouTube', desc: 'Script vidéo longue durée', premium: false },
      { id: 'tiktok-script', name: 'Script TikTok/Reels', desc: 'Script format court viral', premium: false },
      { id: 'explainer-video', name: 'Vidéo explicative', desc: 'Script pour vidéo produit/service', premium: true },
      { id: 'testimonial-script', name: 'Script témoignage', desc: 'Structure pour témoignage client', premium: false }
    ],
    product: [
      { id: 'product-description', name: 'Description produit', desc: 'Fiche produit e-commerce', premium: false },
      { id: 'amazon-listing', name: 'Fiche Amazon', desc: 'Listing optimisé Amazon', premium: true },
      { id: 'shopify-product', name: 'Page Shopify', desc: 'Description Shopify optimisée', premium: false },
      { id: 'product-comparison', name: 'Comparatif produit', desc: 'Tableau comparatif persuasif', premium: true }
    ],
    hooks: [
      { id: 'headlines', name: 'Headlines', desc: 'Titres accrocheurs à fort impact', premium: false },
      { id: 'cta-buttons', name: 'Appels à l\'action', desc: 'CTA persuasifs et variés', premium: false },
      { id: 'hooks-openers', name: 'Hooks d\'ouverture', desc: 'Accroches captivantes', premium: false },
      { id: 'value-props', name: 'Propositions de valeur', desc: 'USP et bénéfices clés', premium: true }
    ]
  };

  const frameworks = [
    { id: 'aida', name: 'AIDA', desc: 'Attention → Intérêt → Désir → Action' },
    { id: 'pas', name: 'PAS', desc: 'Problème → Agitation → Solution' },
    { id: 'fab', name: 'FAB', desc: 'Features → Advantages → Benefits' },
    { id: 'bab', name: 'BAB', desc: 'Before → After → Bridge' },
    { id: '4p', name: '4P', desc: 'Promise → Picture → Proof → Push' },
    { id: 'acca', name: 'ACCA', desc: 'Awareness → Comprehension → Conviction → Action' },
    { id: 'storytelling', name: 'Storytelling', desc: 'Narration émotionnelle' },
    { id: 'pppp', name: 'PPPP', desc: 'Problem → Promise → Proof → Proposal' }
  ];

  const tones = [
    { id: 'professional', name: 'Professionnel', emoji: '👔' },
    { id: 'casual', name: 'Décontracté', emoji: '😊' },
    { id: 'urgent', name: 'Urgent', emoji: '⚡' },
    { id: 'luxurious', name: 'Premium/Luxe', emoji: '✨' },
    { id: 'friendly', name: 'Amical', emoji: '🤝' },
    { id: 'authoritative', name: 'Expert', emoji: '🎓' },
    { id: 'empathetic', name: 'Empathique', emoji: '💚' },
    { id: 'provocative', name: 'Provocateur', emoji: '🔥' }
  ];

  const handleGenerate = async () => {
    if (!activeTemplate || !formData.topic) return;
    
    setGenerating(true);
    setGeneratedContent('');
    setVariants([]);
    setError('');

    try {
      const token = getAuthToken();
      
      // Generate multiple variants if requested
      const allVariants = [];
      
      for (let i = 0; i < variantCount; i++) {
        const response = await fetch(`${API_URL}/api/generate-content`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content_type: activeTemplate,
            topic: formData.topic,
            tone: formData.tone,
            word_count: formData.wordCount,
            industry: formData.targetAudience,
            additional_instructions: `
              Framework: ${formData.framework.toUpperCase()}
              Produit/Service: ${formData.productName}
              Audience cible: ${formData.targetAudience}
              Instructions supplémentaires: ${formData.additionalInfo}
              Variante: ${i + 1}/${variantCount}
              ${variantCount > 1 ? 'Créer une version unique et différente des autres variantes.' : ''}
            `
          })
        });

        if (response.ok) {
          const data = await response.json();
          allVariants.push({
            id: i + 1,
            content: data.content
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Erreur lors de la génération');
        }
      }

      if (allVariants.length > 0) {
        setGeneratedContent(allVariants[0].content);
        setVariants(allVariants);
      }
    } catch (error) {
      console.error('Generation error:', error);
      setError(error.message || 'Une erreur est survenue lors de la génération. Veuillez réessayer.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="grid grid-cols-12 gap-6 h-full">
      {/* Left Panel - Templates */}
      <div className="col-span-3 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-semibold mb-3">Templates</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setActiveTemplate(null); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {templates[activeCategory]?.map(template => (
            <button
              key={template.id}
              onClick={() => setActiveTemplate(template.id)}
              className={`w-full text-left p-3 rounded-xl transition-all ${
                activeTemplate === template.id
                  ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30'
                  : 'bg-gray-800/50 hover:bg-gray-800 border border-transparent'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-medium ${activeTemplate === template.id ? 'text-green-400' : 'text-white'}`}>
                  {template.name}
                </span>
                {template.premium && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-bold">PRO</span>
                )}
              </div>
              <p className="text-xs text-gray-500">{template.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Center Panel - Configuration */}
      <div className="col-span-4 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">Configuration</h3>
          <p className="text-xs text-gray-500 mt-1">Paramétrez votre génération</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Topic */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Sujet / Thème principal *</label>
            <input
              type="text"
              value={formData.topic}
              onChange={(e) => setFormData({...formData, topic: e.target.value})}
              placeholder="Ex: Programme de coaching fitness en ligne"
              data-testid="cw-topic-input"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
            />
          </div>

          {/* Product Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Nom du produit/service</label>
            <input
              type="text"
              value={formData.productName}
              onChange={(e) => setFormData({...formData, productName: e.target.value})}
              placeholder="Ex: FitPro Academy"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
            />
          </div>

          {/* Target Audience */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Audience cible</label>
            <input
              type="text"
              value={formData.targetAudience}
              onChange={(e) => setFormData({...formData, targetAudience: e.target.value})}
              placeholder="Ex: Femmes 25-45 ans, actives, manque de temps"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
            />
          </div>

          {/* Framework */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Framework copywriting</label>
            <div className="grid grid-cols-2 gap-2">
              {frameworks.map(fw => (
                <button
                  key={fw.id}
                  onClick={() => setFormData({...formData, framework: fw.id})}
                  className={`p-2 rounded-lg text-left transition-all ${
                    formData.framework === fw.id
                      ? 'bg-green-500/20 border border-green-500/30'
                      : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <span className={`text-sm font-bold ${formData.framework === fw.id ? 'text-green-400' : 'text-white'}`}>
                    {fw.name}
                  </span>
                  <p className="text-[10px] text-gray-500 mt-0.5">{fw.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Tone */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Ton</label>
            <div className="flex flex-wrap gap-2">
              {tones.map(tone => (
                <button
                  key={tone.id}
                  onClick={() => setFormData({...formData, tone: tone.id})}
                  className={`px-3 py-2 rounded-lg text-sm transition-all ${
                    formData.tone === tone.id
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                  }`}
                >
                  {tone.emoji} {tone.name}
                </button>
              ))}
            </div>
          </div>

          {/* Variants */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Nombre de variantes</label>
            <div className="flex items-center space-x-3">
              {[1, 3, 5, 10].map(num => (
                <button
                  key={num}
                  onClick={() => setVariantCount(num)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    variantCount === num
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Instructions supplémentaires</label>
            <textarea
              value={formData.additionalInfo}
              onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
              placeholder="Informations complémentaires, USP, témoignages à inclure..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all resize-none"
            />
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={generating || !activeTemplate || !formData.topic}
            data-testid="cw-generate-btn"
            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 rounded-xl font-semibold text-white transition-all flex items-center justify-center space-x-2 shadow-lg shadow-green-500/20"
          >
            {generating ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span>Génération en cours...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
                <span>Générer {variantCount > 1 ? `${variantCount} variantes` : 'le contenu'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Right Panel - Output */}
      <div className="col-span-5 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Résultat</h3>
            <p className="text-xs text-gray-500 mt-1">
              {variants.length > 1 ? `${variants.length} variantes générées` : 'Votre contenu généré'}
            </p>
          </div>
          {generatedContent && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(generatedContent)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                title="Copier"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </button>
              <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white" title="Exporter">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Variants Tabs */}
        {variants.length > 1 && (
          <div className="px-4 pt-3 flex space-x-2 overflow-x-auto">
            {variants.map((variant, idx) => (
              <button
                key={variant.id}
                onClick={() => setGeneratedContent(variant.content)}
                data-testid={`cw-variant-${idx + 1}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  generatedContent === variant.content
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                Variante {idx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4" data-testid="cw-output">
          {generating ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <div className="w-16 h-16 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mb-4"></div>
              <p>Génération IA en cours...</p>
              <p className="text-xs mt-2">Framework: {formData.framework.toUpperCase()}</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center text-red-400">
              <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p className="text-center font-medium">{error}</p>
              <button 
                onClick={() => setError('')}
                className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-white transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : generatedContent ? (
            <div className="prose prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                {generatedContent}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p className="text-center">Sélectionnez un template et<br/>configurez vos paramètres</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopywritingModule;
